import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createClerkSupabaseClient } from '@/lib/supabase'
import { supabase } from '@/lib/supabase-auth'
import { useGetToken } from '@/lib/hooks/use-auth'
import { useProject } from '@/contexts/project-context'
import { useUser } from '@/lib/hooks'
import ShareUsageChart from '@/components/share-usage-chart'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Plus,
  FileText,
  BarChart3,
  Download,
  Edit,
  ExternalLink,
  AlertCircle,
  CheckCircle2,
  History
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { toast } from 'sonner'

interface ShareAuthorization {
  id: string
  project_id: string
  authorization_date: string
  total_authorized_shares: number
  common_shares_authorized: number | null
  preferred_shares_authorized: number | null
  option_pool_shares: number | null
  board_approval_date: string | null
  certificate_of_incorporation_url: string | null
  notes: string | null
  created_at: string
  created_by: string
}

interface ShareClass {
  equity_class_id: string
  class_name: string
  outstanding_shares: number
  percentage_of_total: number
}

export default function ShareAuthorizationsPage() {
  const getToken = useGetToken()
  const { currentProject } = useProject()
  const { profile } = useUser()
  const queryClient = useQueryClient()
  const [showNewAuthorizationDialog, setShowNewAuthorizationDialog] = useState(false)
  const [selectedAuthorization, setSelectedAuthorization] = useState<ShareAuthorization | null>(null)
  const [formData, setFormData] = useState({
    authorization_date: new Date().toISOString().split('T')[0],
    total_authorized_shares: '',
    common_shares_authorized: '',
    preferred_shares_authorized: '',
    option_pool_shares: '',
    board_approval_date: '',
    certificate_of_incorporation_url: '',
    notes: ''
  })

  // Fetch authorizations
  const { data: authorizations, isLoading } = useQuery({
    queryKey: ['share-authorizations', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return []
      
      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)
      
      const { data, error } = await authenticatedClient
        .from('share_authorizations')
        .select('*')
        .eq('project_id', currentProject.id)
        .order('authorization_date', { ascending: false })

      if (error) throw error
      return data as unknown as ShareAuthorization[]
    },
    enabled: !!currentProject
  })

  // Fetch share class breakdown
  const { data: shareClasses } = useQuery({
    queryKey: ['share-classes-breakdown', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return [] as ShareClass[]

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .rpc('calculate_outstanding_shares', { p_project_id: currentProject.id })

      if (error) throw error
      return (data || []) as unknown as ShareClass[]
    },
    enabled: !!currentProject
  })

  // Fetch available option pool shares
  const { data: availableOptionPool } = useQuery({
    queryKey: ['available-option-pool', currentProject?.id],
    queryFn: async () => {
      if (!currentProject) return 0

      // Create authenticated Supabase client with Clerk token
      const authenticatedClient = createClerkSupabaseClient(getToken)

      const { data, error } = await authenticatedClient
        .rpc('get_available_option_pool_shares', { p_project_id: currentProject.id })

      if (error) throw error
      return data || 0
    },
    enabled: !!currentProject
  })

  // Create new authorization
  const { mutate: createAuthorization, isPending: isCreating } = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (!currentProject || !profile) throw new Error('No project or user')

      const { error } = await supabase
        .from('share_authorizations')
        .insert({
          project_id: currentProject.id,
          authorization_date: data.authorization_date,
          total_authorized_shares: parseInt(data.total_authorized_shares),
          common_shares_authorized: data.common_shares_authorized ? parseInt(data.common_shares_authorized) : null,
          preferred_shares_authorized: data.preferred_shares_authorized ? parseInt(data.preferred_shares_authorized) : null,
          option_pool_shares: data.option_pool_shares ? parseInt(data.option_pool_shares) : null,
          board_approval_date: data.board_approval_date || null,
          certificate_of_incorporation_url: data.certificate_of_incorporation_url || null,
          notes: data.notes || null,
          created_by: profile.id
        })

      if (error) throw error

      // Update project authorized shares
      const { error: updateError } = await supabase
        .from('projects')
        .update({ authorized_shares: parseInt(data.total_authorized_shares) })
        .eq('id', currentProject.id)

      if (updateError) throw updateError
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-authorizations'] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setShowNewAuthorizationDialog(false)
      resetForm()
      toast.success('Share authorization created successfully')
    },
    onError: () => {
      toast.error('Failed to create share authorization')
    }
  })

  const resetForm = () => {
    setFormData({
      authorization_date: new Date().toISOString().split('T')[0],
      total_authorized_shares: '',
      common_shares_authorized: '',
      preferred_shares_authorized: '',
      option_pool_shares: '',
      board_approval_date: '',
      certificate_of_incorporation_url: '',
      notes: ''
    })
  }

  const handleExport = () => {
    if (!authorizations || authorizations.length === 0) return

    const headers = [
      'Authorization Date',
      'Total Authorized',
      'Common Authorized',
      'Preferred Authorized',
      'Option Pool',
      'Board Approval Date',
      'Notes'
    ]

    const rows = authorizations.map(auth => [
      format(parseISO(auth.authorization_date), 'yyyy-MM-dd'),
      auth.total_authorized_shares,
      auth.common_shares_authorized || '',
      auth.preferred_shares_authorized || '',
      auth.option_pool_shares || '',
      auth.board_approval_date ? format(parseISO(auth.board_approval_date), 'yyyy-MM-dd') : '',
      auth.notes || ''
    ])

    const csv = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `share-authorizations-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const latestAuthorization = authorizations?.[0]
  const totalAuthorized = latestAuthorization?.total_authorized_shares || 0

  // Check if user is owner/admin
  const canManageAuthorizations = profile?.roles?.name === 'owner' || profile?.roles?.name === 'admin'

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Share Authorizations</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage authorized shares and track board resolutions
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          {canManageAuthorizations && (
            <Button onClick={() => setShowNewAuthorizationDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Authorization
            </Button>
          )}
        </div>
      </div>

      {/* Share Usage Charts */}
      {shareClasses && (
        <ShareUsageChart
          authorizedShares={totalAuthorized}
          commonSharesAuthorized={latestAuthorization?.common_shares_authorized || 0}
          preferredSharesAuthorized={latestAuthorization?.preferred_shares_authorized || 0}
          optionPoolShares={latestAuthorization?.option_pool_shares || 0}
          shareClasses={shareClasses}
          availableOptionPoolShares={availableOptionPool || 0}
        />
      )}

      {/* Authorization History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Authorization History</CardTitle>
            <Badge variant="outline" className="text-sm">
              <History className="h-3 w-3 mr-1" />
              {authorizations?.length || 0} amendments
            </Badge>
          </div>
          <CardDescription>
            Track changes to authorized share capital over time
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          ) : authorizations && authorizations.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Total Authorized</TableHead>
                    <TableHead>Common</TableHead>
                    <TableHead>Preferred</TableHead>
                    <TableHead>Options</TableHead>
                    <TableHead>Board Approval</TableHead>
                    <TableHead>Documents</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {authorizations.map((auth, index) => (
                    <TableRow key={auth.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">
                            {format(parseISO(auth.authorization_date), 'MMM d, yyyy')}
                          </p>
                          {index === 0 && (
                            <Badge variant="default" className="text-xs mt-1">Current</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{formatNumber(auth.total_authorized_shares)}</p>
                        {index > 0 && (
                          <p className="text-sm text-gray-500">
                            {auth.total_authorized_shares > (authorizations[index - 1]?.total_authorized_shares || 0) ? '+' : ''}
                            {formatNumber(auth.total_authorized_shares - (authorizations[index - 1]?.total_authorized_shares || 0))}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {auth.common_shares_authorized ? formatNumber(auth.common_shares_authorized) : '-'}
                      </TableCell>
                      <TableCell>
                        {auth.preferred_shares_authorized ? formatNumber(auth.preferred_shares_authorized) : '-'}
                      </TableCell>
                      <TableCell>
                        {auth.option_pool_shares ? formatNumber(auth.option_pool_shares) : '-'}
                      </TableCell>
                      <TableCell>
                        {auth.board_approval_date ? (
                          <div className="flex items-center text-sm">
                            <CheckCircle2 className="h-4 w-4 text-green-500 mr-1" />
                            {format(parseISO(auth.board_approval_date), 'MMM d, yyyy')}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {auth.certificate_of_incorporation_url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(auth.certificate_of_incorporation_url!, '_blank')}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            View
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedAuthorization(auth)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">No share authorizations found</p>
              <p className="text-sm text-gray-400 mt-1">
                Create your first authorization to track share capital
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Authorization Dialog */}
      <Dialog open={showNewAuthorizationDialog} onOpenChange={setShowNewAuthorizationDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Share Authorization</DialogTitle>
            <DialogDescription>
              Record a board resolution to authorize additional shares
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Authorization Date</Label>
                <Input
                  type="date"
                  value={formData.authorization_date}
                  onChange={(e) => setFormData({ ...formData, authorization_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Board Approval Date</Label>
                <Input
                  type="date"
                  value={formData.board_approval_date}
                  onChange={(e) => setFormData({ ...formData, board_approval_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Total Authorized Shares</Label>
              <Input
                type="number"
                value={formData.total_authorized_shares}
                onChange={(e) => setFormData({ ...formData, total_authorized_shares: e.target.value })}
                placeholder="e.g., 15000000"
              />
              <p className="text-sm text-gray-500 mt-1">
                Current: {formatNumber(totalAuthorized)}
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Common Shares</Label>
                <Input
                  type="number"
                  value={formData.common_shares_authorized}
                  onChange={(e) => setFormData({ ...formData, common_shares_authorized: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Preferred Shares</Label>
                <Input
                  type="number"
                  value={formData.preferred_shares_authorized}
                  onChange={(e) => setFormData({ ...formData, preferred_shares_authorized: e.target.value })}
                  placeholder="Optional"
                />
              </div>
              <div>
                <Label>Option Pool</Label>
                <Input
                  type="number"
                  value={formData.option_pool_shares}
                  onChange={(e) => setFormData({ ...formData, option_pool_shares: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div>
              <Label>Certificate of Incorporation URL</Label>
              <Input
                type="url"
                value={formData.certificate_of_incorporation_url}
                onChange={(e) => setFormData({ ...formData, certificate_of_incorporation_url: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Board resolution details, reasons for increase, etc."
                rows={3}
              />
            </div>

            {formData.total_authorized_shares && parseInt(formData.total_authorized_shares) < totalAuthorized && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">Decreasing Authorization</p>
                  <p className="text-sm text-yellow-700">
                    You are decreasing the authorized shares. This is unusual and may require special board approval.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewAuthorizationDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createAuthorization(formData)}
              disabled={!formData.authorization_date || !formData.total_authorized_shares || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create Authorization'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Authorization Details */}
      {selectedAuthorization && (
        <Dialog open={!!selectedAuthorization} onOpenChange={() => setSelectedAuthorization(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Authorization Details</DialogTitle>
              <DialogDescription>
                {format(parseISO(selectedAuthorization.authorization_date), 'MMMM d, yyyy')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm text-gray-500">Total Authorized Shares</p>
                <p className="text-lg font-semibold">{formatNumber(selectedAuthorization.total_authorized_shares)}</p>
              </div>

              {(selectedAuthorization.common_shares_authorized || 
                selectedAuthorization.preferred_shares_authorized || 
                selectedAuthorization.option_pool_shares) && (
                <div className="grid grid-cols-3 gap-4">
                  {selectedAuthorization.common_shares_authorized && (
                    <div>
                      <p className="text-sm text-gray-500">Common</p>
                      <p className="font-medium">{formatNumber(selectedAuthorization.common_shares_authorized)}</p>
                    </div>
                  )}
                  {selectedAuthorization.preferred_shares_authorized && (
                    <div>
                      <p className="text-sm text-gray-500">Preferred</p>
                      <p className="font-medium">{formatNumber(selectedAuthorization.preferred_shares_authorized)}</p>
                    </div>
                  )}
                  {selectedAuthorization.option_pool_shares && (
                    <div>
                      <p className="text-sm text-gray-500">Options</p>
                      <p className="font-medium">{formatNumber(selectedAuthorization.option_pool_shares)}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedAuthorization.board_approval_date && (
                <div>
                  <p className="text-sm text-gray-500">Board Approval Date</p>
                  <p className="font-medium">{format(parseISO(selectedAuthorization.board_approval_date), 'MMMM d, yyyy')}</p>
                </div>
              )}

              {selectedAuthorization.notes && (
                <div>
                  <p className="text-sm text-gray-500">Notes</p>
                  <p className="text-sm">{selectedAuthorization.notes}</p>
                </div>
              )}

              {selectedAuthorization.certificate_of_incorporation_url && (
                <div>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => window.open(selectedAuthorization.certificate_of_incorporation_url!, '_blank')}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    View Certificate of Incorporation
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setSelectedAuthorization(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}
